import React, { useEffect, useState, ChangeEvent, FormEvent, useRef, useCallback } from 'react';
import { NavLink, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAuth } from '../../../../firebase/AuthProvider';
import {
  getAllRoles,
  getUserById,
  updateUser,
} from '../../../../services/userServices/TeamService';
import Loader from '../../loader/Loader';
import Region from '../../../../interfaces/identity/Region';
import { GetReportBody, GetTeamBody, reportTo, teams } from '../../../../interfaces/team/RegionHierarchy';
import { getReportTo, getTeam } from '../../../../services/userServices/UserRolesService';
import { getAllRegions } from '../../../../services/userServices/UserInfoService';
import { Button } from 'react-bootstrap';
import backarrowbtn from '../../../../assets/images/backarrowbtn.svg';
import { ROLE_IDS } from '../../../../Data/SD'; // Add this import if not present
import Waiver from '../../../../interfaces/participant/Waiver';
import { getWaivers } from '../../../../services/participantServices/ParticipantService';
import { isInvalidWaiverFamilySelection } from './AddUser';

interface Role {
  id: number;
  name: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  displayName: string;
  phoneNumber: string;
  scId: string;
  returnSecureToken: boolean;
  organizationId: number;
  regionIds: number[];
  roleIds: number[];
  Createdby: string;
  ManagerId: string;
  teamIds: number[];
  timeZone: string;
  waiverCode? :string;
  waiverFamilyIds: number[];
}

export default function EditUser() {
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view') || 'overview';
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { currentUser } = useAuth();
  const token = (currentUser as any)?.accessToken;
  const userUid = (currentUser as any)?.uid;

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [reportToList, setReportToList] = useState<reportTo[]>([]);
  const [teamsList, setTeamsList] = useState<teams[]>([]);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState<boolean>(false);
  const [regionSearchTerm, setRegionSearchTerm] = useState<string>('');
  const regionDropdownRef = useRef<HTMLDivElement>(null);
  const [teamDropdownOpen, setTeamDropdownOpen] = useState<boolean>(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState<string>('');
  const teamDropdownRef = useRef<HTMLDivElement>(null);
  const [waivers, setWaivers] = useState<Waiver[]>([]);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    displayName: '',
    phoneNumber: '',
    scId: '',
    returnSecureToken: true,
    organizationId: 1,
    Createdby: userUid,
    roleIds: [],
    regionIds: [],
    ManagerId: '',
    teamIds: [],
    timeZone: "",
    waiverCode : "",
    waiverFamilyIds: [],
  });

  // const validateForm = () => {
  //   const newErrors: Record<string, string> = {};
  //   if (!formData.firstName.trim()) newErrors.firstName = 'First Name is required';
  //   if (!formData.lastName.trim()) newErrors.lastName = 'Last Name is required';
  //   if (!formData.displayName.trim()) newErrors.displayName = 'Display Name is required';
  //   if (!formData.email.trim()) newErrors.email = 'Email is required';
  //   else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
  //     newErrors.email = 'Please enter a valid email address';
  //   }
  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };
  const timezone = [
    { label: "Asia/Kolkata (IST)", value: "Asia/Kolkata" },
    { label: "America/Chicago (CST)", value: "America/Chicago" },
    { label: "America/New_York (EST)", value: "America/New_York" },
    { label: "America/Los_Angeles (PST)", value: "America/Los_Angeles" },
    { label: "Europe/London (GMT)", value: "Europe/London" },
    { label: "Asia/Dubai (GST)", value: "Asia/Dubai" },
    { label: "Asia/Singapore (SGT)", value: "Asia/Singapore" },
    { label: "Australia/Sydney (AEST)", value: "Australia/Sydney" },
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Name regex:
    // - Letters only
    // - Allows hyphens & apostrophes
    // - Allows single spaces (no double spaces)
    const nameRegex = /^[A-Za-z'’-]+(?: [A-Za-z'’-]+)*$/;

    const validateName = (value: string, field: "firstName" | "lastName") => {
      const label = field === "firstName" ? "First Name" : "Last Name";

      if (!value.trim()) {
        newErrors[field] = `${label} is required`;
        return;
      }

      const trimmed = value.trim();

      // Max length
      if (trimmed.length > 50) {
        newErrors[field] = `${label} cannot exceed 50 characters`;
        return;
      }

      // Prevent double spaces
      if (/ {2,}/.test(trimmed)) {
        newErrors[field] = `${label} cannot contain multiple consecutive spaces`;
        return;
      }

      // Only valid characters allowed
      if (!nameRegex.test(trimmed)) {
        newErrors[field] = `${label} contains invalid characters`;
        return;
      }
    };

    // Apply name validation
    validateName(formData.firstName, "firstName");
    validateName(formData.lastName, "lastName");

    // Existing validations (unchanged)
    if (!formData.displayName.trim())
      newErrors.displayName = "Display Name is required";

    if (!formData.email.trim())
      newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (
      formData.waiverFamilyIds.length > 0 &&
      isInvalidWaiverFamilySelection(formData.waiverFamilyIds, waivers)
    ) {
      newErrors.waiverFamilyIds = "OAAS and OCDD cannot be selected together";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement> | { target: { name: string; value: any; type?: string } }
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox-group') {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else if (name === 'waiverFamilyIds' && type === 'checkbox-group') {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const trimmedFormData = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        displayName: formData.displayName.trim(),
        waiverFamilyIds: formData.waiverFamilyIds,
      };
      const userUpdateRes = await updateUser(trimmedFormData, userId!, token);
      if (userUpdateRes.success) {
        toast.success(userUpdateRes.message);
        navigate(`/dashboard/owner?view=teams`);
      } else {
        toast.error(userUpdateRes.message);
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      toast.error(error?.message || "Error to update the User")
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(`/dashboard/owner?view=teams`);
  const handleBack = () => navigate(`/dashboard/owner?view=teams`);

  useEffect(() => {
    const fetchAllRolesAndUser = async () => {
      try {
        setLoading(true);
        const allRoles = await getAllRoles(token);
        if (allRoles) {
          setRoles(allRoles);

          const getById = await getUserById(userId!, token);
          if (getById && getById.data) {
            const roleNames = getById.data.roles || [];
            const mappedRoleIds = roleNames.map((name: string) => {
              const role = allRoles.find((r: Role) => r.name.toUpperCase() === name.toUpperCase());
              return role?.id || 0;
            }).filter(id => id !== 0);

            const regionIdsFromApi = (getById.data.regions || []).map((region: any) => region.id);

            // Initialize waiverFamilyIds from API response
            let waiverFamilyIds: number[] = [];
            
            // API returns waiverFamilyIds directly as array
            console.log('API Response waiverFamilyIds:', getById.data.waiverFamilyIds);
            console.log('API Response waiverFamilyCode:', getById.data.waiverFamilyCode);
            
            if (getById.data.waiverFamilyIds && Array.isArray(getById.data.waiverFamilyIds)) {
              waiverFamilyIds = getById.data.waiverFamilyIds;
              console.log('Setting waiverFamilyIds to:', waiverFamilyIds);
            } else {
              console.log('No waiverFamilyIds found in API response');
            }

            setFormData(prev => ({
              ...prev,
              firstName: getById.data.firstName || '',
              lastName: getById.data.lastName || '',
              email: getById.data.email || '',
              displayName: getById.data.displayName || '',
              phoneNumber: getById.data.phoneNumber || '',
              scId: getById.data.scId || '',
              returnSecureToken: true,
              organizationId: getById.data.organizationId || 1,
              regionIds: regionIdsFromApi || [],
              roleIds: mappedRoleIds,
              ManagerId: getById.data.managerId || '',
              teamIds: getById.data.teams?.map((team: any) => team.id) || [],
              timeZone: getById.data.timezone || "",
              waiverFamilyIds: waiverFamilyIds,
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching roles or user:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllRolesAndUser();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      setLoading(true);
      try {
        const authToken = await currentUser.getIdToken();
        if (!authToken) {
          console.warn("No auth token available for fetching data.");
          toast.warn("Authentication details missing.");
          return;
        }

        const regionsData = await getAllRegions(authToken);
        if (regionsData) setRegions(regionsData.data);
        else toast.error("Failed to load regions.");
      } catch (error) {
        console.error("Error fetching regions:", error);
        toast.error("Error fetching regions.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    GetWaiver();
  }, [currentUser]);

  const fetchRoleRegionRelatedData = async () => {
    if (!currentUser || !formData.regionIds.length || !formData.roleIds.length) return;

    const authToken = await currentUser.getIdToken();
    const requestBody: GetReportBody = {
      regionIds: formData.regionIds,
      roleIds: formData.roleIds,
      teamIds: formData.teamIds
    };
    const requestBodyforTeam: GetTeamBody = {
      regionIds: formData.regionIds,
      roleIds: formData.roleIds,
    };

    try {
      const [reportToRes, teamsRes] = await Promise.all([
        getReportTo(authToken, requestBody),
        getTeam(authToken, requestBodyforTeam),
      ]);
      if (reportToRes.success) setReportToList(reportToRes.data);
      if (teamsRes) setTeamsList(teamsRes);
    } catch (error) {
      toast.error('Failed to fetch teams or reporting users.');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchRoleRegionRelatedData();
  }, [formData.regionIds, formData.roleIds]);

    const GetWaiver = async () => {
        const token = await currentUser?.getIdToken();
        if (!token) return;
        const res = await getWaivers(token)
        if (res.success) {
            setWaivers(res.data);
        }
    }

  const handleSelectAllRegions = useCallback(() => {
    const allRegionIds = regions.map((r) => r.regionId);
    handleChange({ target: { name: 'regionIds', value: allRegionIds, type: 'checkbox-group' } });
  }, [regions]);

  const handleUnselectAllRegions = useCallback(() => {
    handleChange({ target: { name: 'regionIds', value: [], type: 'checkbox-group' } });
  }, []);

  const toggleRegion = useCallback(
    (id: number) => {
      const updated = formData.regionIds.includes(id)
        ? formData.regionIds.filter((rid) => rid !== id)
        : [...formData.regionIds, id];
      handleChange({ target: { name: 'regionIds', value: updated, type: 'checkbox-group' } });
    },
    [formData.regionIds]
  );

  const filteredRegions = regions.filter((region) =>
    region.regionName.toLowerCase().includes(regionSearchTerm.toLowerCase())
  );

  // Handle closing both dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Close Team dropdown if click is outside of its container
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(target)) {
        setTeamDropdownOpen(false);
      }

      // Close Region dropdown if click is outside of its container
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(target)) {
        setRegionDropdownOpen(false);
      }
    };

    // Attach event listener when component mounts
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup event listener when component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  const handleSelectAllTeams = useCallback(() => {
    const allTeamIds = teamsList.map((t) => t.id);
    handleChange({ target: { name: 'teamIds', value: allTeamIds, type: 'checkbox-group' } });
  }, [teamsList]);

  const handleUnselectAllTeams = useCallback(() => {
    handleChange({ target: { name: 'teamIds', value: [], type: 'checkbox-group' } });
  }, []);

  const toggleTeam = useCallback(
    (id: number) => {
      const updated = formData.teamIds.includes(id)
        ? formData.teamIds.filter((tid) => tid !== id)
        : [...formData.teamIds, id];
      handleChange({ target: { name: 'teamIds', value: updated, type: 'checkbox-group' } });
    },
    [formData.teamIds]
  );

  const filteredTeams = teamsList.filter((team) =>
    team.teamName.toLowerCase().includes(teamSearchTerm.toLowerCase())
  );

  // Fetch teams when regionIds change
  useEffect(() => {
    const fetchTeamsList = async () => {
      if (!currentUser || !formData.regionIds.length) return;
      const authToken = await currentUser.getIdToken();
      if (!authToken) return;
      try {
        // You may use getAllTeamByRegions if available, else fallback to getTeam
        const teamsRes = await getTeam(authToken, { regionIds: formData.regionIds, roleIds: formData.roleIds });
        if (teamsRes) setTeamsList(teamsRes);
      } catch (error) {
        toast.error('Failed to fetch teams.');
      }
    };
    fetchTeamsList();
  }, [formData.regionIds, currentUser, formData.roleIds]);

  // Fetch reportTo when roleIds, regionIds, or teamIds change
  useEffect(() => {
    const fetchReportToList = async () => {
      if (!currentUser) return;
      const authToken = await currentUser.getIdToken();
      if (!authToken || !formData.regionIds.length || !formData.roleIds.length || (!formData.teamIds.length && !formData.roleIds.includes(ROLE_IDS.PROGRAM_MANAGER))) return;

      const requestBody: GetReportBody = {
        regionIds: formData.regionIds,
        roleIds: formData.roleIds,
        teamIds: formData.teamIds,
      };

      try {
        setReportToList([]);
        const reportToRes = await getReportTo(authToken, requestBody);
        if (reportToRes?.success) {
          setReportToList(reportToRes.data);
          if (!reportToRes.data.some(m => m.id == formData.ManagerId)) {
            handleChange({ target: { name: "ManagerId", value: "", type: "string" } });
          }
        } else {
          toast.error(reportToRes?.message);
        }
      } catch (error) {
        toast.error('Failed to fetch reporting users.');
      }
    };
    fetchReportToList();
  }, [formData.roleIds, formData.regionIds, formData.teamIds, currentUser]);

  return (
    <>
      {/* <div className="flex gap-5 text-xl">
        <NavLink
          to="?view=teams"
          className={`sub-nav-item ${view === 'teams' ? 'sub-nav-active' : ''}`}
        >
          Teams
        </NavLink>
      </div> */}
      <div className="new_activity_container">
        <div className='common_card'>
          <div className="header-user">
            <div className="top border_tittle">
              <div className="top_bar_tittle">
                <h2><button onClick={handleBack}><img src={backarrowbtn} alt="backarrowbtn" /></button>Edit User</h2>
              </div>
            </div>
          </div>
          <div>
            {loading && <Loader />}
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* First Name */}
                <div className="col-md-4">
                  <div className='custom_input_flled'>
                    <label>First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Enter first name"
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm">{errors.firstName}</p>
                    )}
                  </div>
                </div>

                {/* Last Name */}
                <div className="col-md-4">
                  <div className='custom_input_flled'>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Enter last name"
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="col-md-4">
                  <div className='custom_input_flled'>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      readOnly
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter email"
                    />
                    {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                  </div>
                </div>

                {/* Display Name */}
                <div className="col-md-4">
                  <div className='custom_input_flled'>
                    <label>Display Name</label>
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      placeholder="Enter display name"
                    />
                    {errors.displayName && (
                      <p className="text-red-500 text-sm">{errors.displayName}</p>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className='custom_input_flled'>
                        <label>Waiver Family</label>
                        <div className="space-y-2">
                            {waivers.length > 0 ? (
                                <>

                                    {waivers.map((waiver) => (
                                        <label key={waiver.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                                            <input
                                                type="checkbox"
                                                checked={formData.waiverFamilyIds.includes(waiver.id)}
                                                onChange={() => {
                                                    const updated = formData.waiverFamilyIds.includes(waiver.id)
                                                        ? formData.waiverFamilyIds.filter((wid) => wid !== waiver.id)
                                                        : [...formData.waiverFamilyIds, waiver.id];
                                                    console.log('Checkbox clicked:', waiver.waiverName, 'Current formData.waiverFamilyIds:', formData.waiverFamilyIds);
                                                    handleChange({
                                                        target: { name: "waiverFamilyIds", value: updated, type: "checkbox-group" }
                                                    });
                                                }}
                                                className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                            />
                                            <span className="text-sm text-gray-700">{waiver.waiverName}</span>
                                            {waiver.waiverCode && (
                                                <span className="ml-2 text-xs text-gray-500">({waiver.waiverCode})</span>
                                            )}
                                        </label>
                                    ))}
                                </>

                            ) : (
                                <p className="text-sm text-gray-500">No waiver families available</p>
                            )}
                        </div>
                        {errors.waiverFamilyIds && (
                            <p className="text-red-500 text-sm">{errors.waiverFamilyIds}</p>
                        )}
                    </div>
                    </div>

                {/* Employee ID */}
                <div className="col-md-4">
                  <div className='custom_input_flled'>
                    <label>Employee ID</label>
                    <input
                      type="text"
                      name="scId"
                      readOnly
                      value={formData.scId}
                      onChange={handleChange}
                      placeholder="Enter employee Id"
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div className="col-md-4">
                  <div className='custom_input_flled'>
                    <label>Phone No</label>
                    <input
                      type="text"
                      name="phoneNumber"
                      maxLength={10}
                      readOnly
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>

                {/* Roles */}
                <div className="col-md-4">
                  <div className='custom_input_flled'>
                    <label>Role</label>
                    <div className="w-full border rounded px-3 py-2 text-sm bg-gray-100 text-gray-700 cursor-not-allowed">
                      {formData.roleIds.length > 0
                        ? roles
                          .filter((r) => formData.roleIds.includes(r.id))
                          .map((r) => r.name)
                          .join(', ')
                        : 'No roles assigned'}
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="relative" ref={regionDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                    <div
                      onClick={() => setRegionDropdownOpen(!regionDropdownOpen)}
                      className="w-full border rounded px-3 py-2 text-sm cursor-pointer bg-white min-h-[38px] flex items-center"
                      aria-haspopup="listbox"
                      aria-expanded={regionDropdownOpen}
                    >
                      {formData.regionIds.length > 0
                        ? regions
                          .filter((r) => formData.regionIds.includes(r.regionId))
                          .map((r) => r.regionName)
                          .join(", ")
                        : <span className="text-gray-500">Select region(s)</span>}
                    </div>

                    {regionDropdownOpen && (
                      <div className="absolute z-10 w-full bg-white border mt-1 rounded shadow max-h-72 overflow-y-auto">
                        <div className="p-2 border-b">
                          <input
                            type="text"
                            placeholder="Search regions..."
                            value={regionSearchTerm}
                            onChange={(e) => setRegionSearchTerm(e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                        </div>
                        <div className="flex items-center justify-between px-3 py-2 border-b text-sm">
                          <button type="button" onClick={handleSelectAllRegions} className="text-blue-600 hover:underline">Select All</button>
                          <button type="button" onClick={handleUnselectAllRegions} className="text-red-500 hover:underline">Unselect All</button>
                        </div>
                        {filteredRegions.length > 0 ? (
                          filteredRegions.map((region) => (
                            <label key={region.regionId} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm">
                              <input
                                type="checkbox"
                                checked={formData.regionIds.includes(region.regionId)}
                                onChange={() => toggleRegion(region.regionId)}
                                className="mr-2"
                              />
                              {region.regionName}
                            </label>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-400 text-sm">No regions found</div>
                        )}
                      </div>
                    )}
                    {errors.regionIds && <p className="text-red-500 text-sm mt-1">{errors.regionIds}</p>}
                  </div>
                </div>
                {/* Teams dropdown - hide if Program Manager */}
                {
                  !formData.roleIds.includes(ROLE_IDS.PROGRAM_MANAGER) &&
                  <div className="col-md-4">
                    <div className="relative" ref={teamDropdownRef}>
                      <label>Teams</label>
                      <div
                        onClick={() => setTeamDropdownOpen(!teamDropdownOpen)}
                        className="w-full border rounded px-3 py-2 text-sm cursor-pointer bg-white min-h-[38px] flex items-center"
                        aria-haspopup="listbox"
                        aria-expanded={teamDropdownOpen}
                      >
                        {formData.teamIds.length > 0
                          ? teamsList
                            .filter((t) => formData.teamIds.includes(t.id))
                            .map((t) => t.teamName)
                            .join(", ")
                          : <span className="text-gray-500">Select team(s)</span>}
                      </div>
                      {teamDropdownOpen && (
                        <div className="absolute z-10 w-full bg-white border mt-1 rounded shadow max-h-72 overflow-y-auto">
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              placeholder="Search teams..."
                              value={teamSearchTerm}
                              onChange={(e) => setTeamSearchTerm(e.target.value)}
                              className="w-full px-2 py-1 border rounded text-sm"
                            />
                          </div>
                          <div className="flex items-center justify-between px-3 py-2 border-b text-sm">
                            <button type="button" onClick={handleSelectAllTeams} className="text-blue-600 hover:underline">Select All</button>
                            <button type="button" onClick={handleUnselectAllTeams} className="text-red-500 hover:underline">Unselect All</button>
                          </div>
                          {filteredTeams.length > 0 ? (
                            filteredTeams.map((team) => (
                              <label key={team.id} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm">
                                <input
                                  type="checkbox"
                                  checked={formData.teamIds.includes(team.id)}
                                  onChange={() => toggleTeam(team.id)}
                                  className="mr-2"
                                />
                                {team.teamName}
                              </label>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-gray-400 text-sm">No teams found</div>
                          )}
                        </div>
                      )}
                      {errors.teamIds && (
                        <p className="text-red-500 text-sm mt-1">{errors.teamIds}</p>
                      )}
                    </div>
                  </div>
                }

                {/* Manager dropdown */}
                <div className="col-md-4">
                  <div className='custom_input_flled'>
                    <label htmlFor="reportTo">Manager</label>
                    <select
                      id="ManagerId"
                      name="ManagerId"
                      value={formData.ManagerId}
                      onChange={handleChange}
                    >
                      <option value="">Select a Report-To</option>
                      {reportToList?.map((repot) => (
                        <option key={repot.id} value={repot.id}>
                          {repot.fullName}
                        </option>
                      ))}
                    </select>
                    {errors.ManagerId && (
                      <p className="text-red-500 text-sm mt-1">{errors.ManagerId}</p>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="custom_input_flled">
                    <label>Time Zone</label>
                    <select
                      name="timeZone"
                      value={formData.timeZone}
                      onChange={handleChange}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="">Select Time Zone</option>

                      {timezone.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>


              </div>
              {/* Buttons */}
              <div className="form_btn_flx">
                <Button variant='secondary' type="submit">Save </Button>
                <Button variant='primary' type="button" onClick={handleCancel} className="form_btn_flx">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
